import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import { MembershipTierKey } from '@/config/communityMembership';

interface UseMembershipTierReturn {
  /** The effective tier (respects admin "view as" override) */
  tier: MembershipTierKey | null;
  /** The actual tier from the database (ignores "view as") */
  realTier: MembershipTierKey | null;
  isBeta: boolean;
  isRegular: boolean;
  isLoading: boolean;
  refreshTier: () => Promise<void>;
}

/**
 * Hook for checking the current user's membership tier.
 *
 * Respects the admin "view as" mode:
 * - viewAsRole === 'admin' → returns the real DB tier
 * - viewAsRole === 'beta'  → returns 'beta' regardless of DB
 * - viewAsRole === 'regular' → returns 'regular' regardless of DB
 *
 * Usage:
 *   const { isBeta } = useMembershipTier();
 *   if (isBeta) { /* show beta content *\/ }
 */
export function useMembershipTier(): UseMembershipTierReturn {
  const { user, isAuthenticated, isAdmin, viewAsRole } = useAuth();
  const [realTier, setRealTier] = useState<MembershipTierKey | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTier = useCallback(async () => {
    if (!user?.id) {
      setRealTier(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('membership_tier')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching membership tier:', error);
        return;
      }

      if (data) {
        setRealTier((data.membership_tier as MembershipTierKey) ?? 'beta');
      }
    } catch (error) {
      console.error('Error in fetchTier:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const refreshTier = useCallback(async () => {
    setIsLoading(true);
    await fetchTier();
  }, [fetchTier]);

  // Apply "view as" override for admins
  const tier = useMemo(() => {
    if (isAdmin && viewAsRole !== 'admin') {
      return viewAsRole as MembershipTierKey;
    }
    return realTier;
  }, [isAdmin, viewAsRole, realTier]);

  const isBeta = useMemo(() => tier === 'beta', [tier]);
  const isRegular = useMemo(() => tier === 'regular', [tier]);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      fetchTier();
    } else {
      setRealTier(null);
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.id, fetchTier]);

  return {
    tier,
    realTier,
    isBeta,
    isRegular,
    isLoading,
    refreshTier,
  };
}
